import { useContext, useEffect, useState } from "react";
import { AppContext } from "../App";
import banner3 from "../assets/banner3.png";
import { 
  FaShieldAlt, 
  FaUsers, 
  FaHardHat, 
  FaCheckCircle,
  FaArrowRight,
  FaAward,
  FaClock,
  FaChartLine
} from "react-icons/fa";
import AOS from "aos";
import "aos/dist/aos.css";

export default function HomeAboutSection() {
  const appContext = useContext(AppContext);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    AOS.init({ once: true, offset: 100, easing: "ease-out-cubic", duration: 800 });
    
    // Intersection Observer for fade-in effect
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );
    
    const section = document.querySelector('.about-modern-section');
    if (section) observer.observe(section);
    
    return () => observer.disconnect();
  }, []);

  return (
    <>
      {/* Modern CSS Styles */}
      <style>{`
        /* Section Base */
        .about-modern-section {
          position: relative;
          padding: 6rem 0;
          background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
          overflow: hidden;
        }

        /* Decorative Background Elements */
        .about-bg-accent {
          position: absolute;
          border-radius: 50%;
          filter: blur(100px);
          opacity: 0.08;
          pointer-events: none;
        }
        .about-bg-accent.one {
          width: 500px;
          height: 500px;
          background: linear-gradient(135deg, #0d6efd, #6ea8fe);
          top: -150px;
          right: -100px;
        }
        .about-bg-accent.two {
          width: 400px;
          height: 400px;
          background: linear-gradient(135deg, #198754, #4ade80);
          bottom: -120px;
          left: -80px;
        }

        /* Grid Pattern */
        .about-grid-pattern {
          position: absolute;
          inset: 0;
          background-image: 
            linear-gradient(rgba(13, 110, 253, 0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(13, 110, 253, 0.03) 1px, transparent 1px);
          background-size: 60px 60px;
          pointer-events: none;
        }

        /* Content Container */
        .about-content-wrapper {
          position: relative;
          z-index: 2;
        }

        /* Section Header */
        .about-section-header {
          text-align: center;
          max-width: 700px;
          margin: 0 auto 4rem;
        }
        .about-kicker {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          background: linear-gradient(135deg, rgba(13, 110, 253, 0.1), rgba(13, 110, 253, 0.05));
          color: #0d6efd;
          padding: 8px 22px;
          border-radius: 50px;
          font-weight: 600;
          font-size: 0.85rem;
          letter-spacing: 0.5px;
          text-transform: uppercase;
          margin-bottom: 1.25rem;
          border: 1px solid rgba(13, 110, 253, 0.15);
        }
        .about-title {
          font-size: clamp(2rem, 4.5vw, 3rem);
          font-weight: 800;
          line-height: 1.15;
          color: #1a1a2e;
          margin-bottom: 1rem;
          letter-spacing: -0.02em;
        }
        .about-title-gradient {
          background: linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .about-subtitle {
          font-size: 1.15rem;
          color: #6c757d;
          line-height: 1.7;
        }

        /* Image Stack - Enhanced */
        .about-image-stack {
          position: relative;
          border-radius: 28px;
          overflow: hidden;
          box-shadow: 0 10px 15px rgba(13, 110, 253, 0.15);
          transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .about-image-stack:hover {
          transform: translateY(-8px);
          box-shadow: 0 35px 100px rgba(13, 110, 253, 0.22);
        }
        .about-main-image {
          width: 100%;
          height: 480px;
          object-fit: cover;
          transition: transform 0.6s ease;
        }
        .about-image-stack:hover .about-main-image {
          transform: scale(1.05);
        }
        .about-image-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(
            to top,
            rgba(10, 25, 47, 0.7) 0%,
            transparent 60%
          );
        }

        /* Floating Info Card */
        .about-float-card {
          position: absolute;
          bottom: 24px;
          left: 24px;
          right: 24px;
          background: rgba(255, 255, 255, 0.98);
          backdrop-filter: blur(20px);
          border-radius: 20px;
          padding: 1.5rem;
          box-shadow: 0 15px 50px rgba(0, 0, 0, 0.15);
          border: 1px solid rgba(255, 255, 255, 0.8);
          transition: all 0.3s ease;
        }
        .about-float-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
        }
        .about-float-kicker {
          font-size: 0.8rem;
          font-weight: 600;
          color: #0d6efd;
          text-transform: uppercase;
          letter-spacing: 0.8px;
          margin-bottom: 0.5rem;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .about-float-title {
          font-size: 1.15rem;
          font-weight: 700;
          color: #1a1a2e;
          margin-bottom: 0.5rem;
          line-height: 1.35;
        }
        .about-float-desc {
          font-size: 0.95rem;
          color: #6c757d;
          line-height: 1.6;
          margin: 0;
        }

        /* Stats Badge */
        .about-stats-badge {
          position: absolute;
          top: 24px;
          right: 24px;
          background: rgba(255, 255, 255, 0.98);
          backdrop-filter: blur(16px);
          border-radius: 18px;
          padding: 14px 20px;
          box-shadow: 0 10px 35px rgba(0, 0, 0, 0.12);
          border: 1px solid rgba(255, 255, 255, 0.9);
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
          transition: all 0.3s ease;
        }
        .about-stats-badge:hover {
          transform: scale(1.05);
        }
        .about-stats-number {
          font-size: 1.75rem;
          font-weight: 800;
          background: linear-gradient(135deg, #0d6efd, #0a58ca);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          line-height: 1;
        }
        .about-stats-label {
          font-size: 0.75rem;
          font-weight: 600;
          color: #6c757d;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        /* Content Card - Enhanced */
        .about-content-card {
          background: #fff;
          border-radius: 28px;
          padding: 2.5rem;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.08);
          border: 1px solid rgba(0, 0, 0, 0.04);
          height: 100%;
          transition: all 0.3s ease;
        }
        .about-content-card:hover {
          box-shadow: 0 25px 70px rgba(13, 110, 253, 0.12);
        }

        /* Content Header */
        .about-content-header {
          margin-bottom: 2rem;
          padding-bottom: 1.5rem;
          border-bottom: 1px solid rgba(0, 0, 0, 0.06);
        }
        .about-content-title {
          font-size: 1.75rem;
          font-weight: 700;
          color: #1a1a2e;
          margin-bottom: 0.75rem;
          line-height: 1.3;
        }
        .about-content-subtitle {
          font-size: 1.05rem;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        /* Content Text */
        .about-content-text {
          font-size: 1.05rem;
          color: #495057;
          line-height: 1.85;
          margin-bottom: 2rem;
        }

        /* Value Cards Grid - Enhanced */
        .about-values-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
          margin-bottom: 2.5rem;
        }
        .value-card {
          display: flex;
          align-items: flex-start;
          gap: 14px;
          padding: 18px;
          background: linear-gradient(135deg, #f8f9ff 0%, #ffffff 100%);
          border-radius: 16px;
          border: 1px solid rgba(13, 110, 253, 0.08);
          transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .value-card:hover {
          background: linear-gradient(135deg, #ffffff 0%, #f0f4ff 100%);
          border-color: rgba(13, 110, 253, 0.2);
          transform: translateY(-4px);
          box-shadow: 0 12px 35px rgba(13, 110, 253, 0.1);
        }
        .value-card-icon {
          width: 48px;
          height: 48px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          font-size: 1.25rem;
        }
        .value-card-icon.one {
          background: linear-gradient(135deg, #0d6efd, #6ea8fe);
          color: #fff;
          box-shadow: 0 8px 20px rgba(13, 110, 253, 0.35);
        }
        .value-card-icon.two {
          background: linear-gradient(135deg, #198754, #4ade80);
          color: #fff;
          box-shadow: 0 8px 20px rgba(25, 135, 84, 0.35);
        }
        .value-card-icon.three {
          background: linear-gradient(135deg, #6f42c1, #a78bfa);
          color: #fff;
          box-shadow: 0 8px 20px rgba(111, 66, 193, 0.35);
        }
        .value-card-icon.four {
          background: linear-gradient(135deg, #fd7e14, #ffc107);
          color: #fff;
          box-shadow: 0 8px 20px rgba(253, 126, 20, 0.35);
        }
        .value-card-content strong {
          display: block;
          font-size: 1rem;
          font-weight: 700;
          color: #1a1a2e;
          margin-bottom: 4px;
        }
        .value-card-content span {
          font-size: 0.9rem;
          color: #6c757d;
          line-height: 1.4;
        }

        /* Quick Stats Row */
        .about-quick-stats {
          display: flex;
          flex-wrap: wrap;
          gap: 20px;
          padding: 1.5rem;
          background: rgba(13, 110, 253, 0.04);
          border-radius: 16px;
          margin-bottom: 2rem;
          border: 1px solid rgba(13, 110, 253, 0.08);
        }
        .quick-stat {
          display: flex;
          align-items: center;
          gap: 10px;
          flex: 1;
          min-width: 140px;
        }
        .quick-stat-icon {
          width: 40px;
          height: 40px;
          border-radius: 12px;
          background: rgba(13, 110, 253, 0.1);
          display: flex;
          align-items: center;
          justify-content: center;
          color: #0d6efd;
          font-size: 1.1rem;
        }
        .quick-stat-content strong {
          display: block;
          font-size: 1.25rem;
          font-weight: 700;
          color: #1a1a2e;
          line-height: 1;
        }
        .quick-stat-content span {
          font-size: 0.85rem;
          color: #6c757d;
          font-weight: 500;
        }

        /* CTA Button - Enhanced */
        .about-cta-btn {
          display: inline-flex;
          align-items: center;
          gap: 12px;
          background: linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%);
          color: #fff;
          padding: 16px 32px;
          border-radius: 14px;
          font-weight: 600;
          font-size: 1.05rem;
          border: none;
          cursor: pointer;
          transition: all 0.35s cubic-bezier(0.16, 1, 0.3, 1);
          box-shadow: 0 12px 40px rgba(13, 110, 253, 0.4);
        }
        .about-cta-btn:hover {
          transform: translateY(-3px);
          box-shadow: 0 18px 55px rgba(13, 110, 253, 0.55);
        }
        .about-cta-btn svg {
          transition: transform 0.3s ease;
        }
        .about-cta-btn:hover svg {
          transform: translateX(6px);
        }

        /* Divider */
        .about-section-divider {
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(13, 110, 253, 0.15), transparent);
          margin-top: 4rem;
        }

        /* Responsive */
        @media (max-width: 991px) {
          .about-modern-section { padding: 4rem 0; }
          .about-main-image { height: 380px; }
          .about-content-card { padding: 2rem; }
          .about-values-grid { grid-template-columns: 1fr; }
          .about-quick-stats { flex-direction: column; gap: 16px; }
          .quick-stat { width: 100%; }
        }
        @media (max-width: 575px) {
          .about-modern-section { padding: 3rem 0; }
          .about-content-card { padding: 1.5rem; border-radius: 20px; }
          .about-main-image { height: 320px; }
          .about-float-card { 
            left: 16px; 
            right: 16px; 
            bottom: 16px; 
            padding: 1.25rem;
          }
          .about-stats-badge {
            top: 16px;
            right: 16px;
            padding: 12px 16px;
          }
          .about-title { font-size: clamp(1.75rem, 5vw, 2.4rem); }
          .about-cta-btn { width: 100%; justify-content: center; }
        }
      `}</style>

      <section className="about-modern-section" ref={(el) => el && setIsVisible(true)}>
        {/* Decorative Background */}
        <div className="about-bg-accent one" />
        <div className="about-bg-accent two" />
        <div className="about-grid-pattern" />

        <div className="container px-3 px-md-4 about-content-wrapper">
          
          {/* Section Header */}
          <div className="about-section-header" data-aos="fade-up">
            
            <h2 className="about-title">
              Engineering Excellence with{" "}
              <span className="about-title-gradient text-primary font-bold">National Impact</span>
            </h2>
            <p className="about-subtitle">
              Reliable solutions. Proven execution. Trusted partnership.
            </p>
          </div>

          <div className="row align-items-start g-4 g-lg-5">
            
            {/* LEFT: Image Stack */}
            <div className="col-lg-6 h-100" data-aos="fade-right" data-aos-delay="100">
              <div className="about-image-stack">
                <img 
                  src={banner3} 
                  alt="Medal Global Engineering team at work" 
                  className="about-main-image" 
                />
                <div className="about-image-overlay" />
                
                {/* Stats Badge (Top Right) */}
                <div className="about-stats-badge">
                  <span className="about-stats-number">15+</span>
                  <span className="about-stats-label">Years</span>
                </div>

                {/* Floating Info Card (Bottom) */}
                <div className="about-float-card">
                  <div className="about-float-kicker">
                    <FaAward /> Infrastructure Focus
                  </div>
                  <h4 className="about-float-title">
                    Powering Tanzania's growth through engineering
                  </h4>
                  <p className="about-float-desc">
                    We partner with public and private clients to design, build, and maintain critical infrastructure nationwide.
                  </p>
                </div>
              </div>
            </div>

            {/* RIGHT: Content Card */}
            <div className="col-lg-6 h-100" data-aos="fade-left" data-aos-delay="200">
              <div className="about-content-card">
                
                {/* Content Header */}
                <div className="about-content-header">
                  <h3 className="about-content-title text-secondary font-bold">
                    Building Tanzania's future, one project at a time
                  </h3>
                  <p className="about-content-subtitle text-primary font-semi-bold">
                    Certified • Licensed • Trusted
                  </p>
                </div>

                {/* Content Text */}
                <p className="about-content-text">
                  Medal Global Engineering Ltd. is a registered Tanzanian engineering company 
                  delivering specialized solutions in electrical engineering, civil construction, 
                  and industrial maintenance. We combine technical expertise with local knowledge 
                  to execute projects that meet international standards while serving our 
                  community's unique needs.
                </p>

                {/* Quick Stats Row */}
                <div className="about-quick-stats">
                  <div className="quick-stat">
                    <div className="quick-stat-icon"><FaChartLine /></div>
                    <div className="quick-stat-content">
                      <strong>200+</strong>
                      <span>Projects Done</span>
                    </div>
                  </div>
                  <div className="quick-stat">
                    <div className="quick-stat-icon"><FaClock /></div>
                    <div className="quick-stat-content">
                      <strong>99%</strong>
                      <span>On-Time Delivery</span>
                    </div>
                  </div>
                </div>

              </div>
            </div>

          </div>

          {/* Section Divider */}
          <div className="about-section-divider" />

        </div>
      </section>
    </>
  );
}